import React, { useCallback, useEffect, useRef, useMemo, FC } from 'react';
import noop from 'lodash/noop';
import { useDispatch } from 'react-redux';

import useBeforeUnloadConfirm from 'ia-react-core/~/hooks/useBeforeUnloadConfirm';
import Router from 'ia-react-core/~/services/router/Router';
import { HistoryLocation } from 'ia-react-core/~/interfaces/HistoryLocation';
import ExitModal, { ExitModalProps } from '../../modal/ExitModal';
import openModalByName from '../../../actions/modal-fn/openModalByName';
import closeModalByName from '../../../actions/modal-fn/closeModalByName';
import useScopedSelector from '../../../hooks/useScopedSelector';
import useTranslation from '../../../hooks/useTranslation';
import useFormContext from '../../../hooks/useFormContext';

export interface FormGuardProps extends ExitModalProps {
  name?: string;
  resourceSrc?: string;
  message?: string;
}

const FormGuard: FC<FormGuardProps> = ({ name = 'exitModal', resourceSrc, ...props }) => {
  const { t } = useTranslation('FormGuard');
  const nextLocationRef = useRef<HistoryLocation>();
  const confirmedRef = useRef(false);
  const history = Router.useHistory();
  const dispatch = useDispatch();
  const { formReducerKey } = useFormContext();
  const resolveResourceSrc = useMemo(() => formReducerKey || resourceSrc, [formReducerKey, resourceSrc]);
  const isDeeplyDirty: boolean = useScopedSelector(`${resolveResourceSrc}.isDeeplyDirty`);

  useBeforeUnloadConfirm(t('message'), isDeeplyDirty);

  const handleConfirm = useCallback(() => {
    dispatch(closeModalByName(name));
    confirmedRef.current = true;
    history.push(`${nextLocationRef.current.pathname}${nextLocationRef.current.search}`);
  }, [dispatch, history, name]);

  useEffect(() => {
    if (isDeeplyDirty) {
      // eslint-disable-next-line consistent-return
      const unblock = history.block((newLocation) => {
        if (!confirmedRef.current) {
          nextLocationRef.current = newLocation;
          dispatch(openModalByName(name));
          return false;
        }

        confirmedRef.current = false;
      });

      return () => {
        unblock();
      };
    }
    return noop;
  }, [dispatch, history, isDeeplyDirty, name]);

  return (
    <ExitModal
      name={name}
      onConfirm={handleConfirm}
      {...props}
    />
  );
};

export default FormGuard;
